import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react'
import {
  Modal,
  Button,
  Slider,
  Tooltip,
  Icon,
  Input,
  Spin,
  message,
  Badge
} from 'antd'
import {
  editImageDeepAI,
  generateImageWithReferenceNanoBanana,
  getTaskStatus
} from '../../../../../../services/api/aiVideoGeneration'
import { ImagePreview } from '../../../../../../components'
import { createCreativeGuidelineSessionPayload } from '../../utils/payloadHelpers'
import ReferenceSearch from './ReferenceSearch'
import ImageUploader from './ImageUploader'
import DrawingCanvas from './DrawingCanvas'
import './ImageEditorModal.scss'

const { TextArea } = Input
const EMPTY_REFERENCE_IMAGES = []
const REFERENCE_GRID_COLUMNS = 2
const REFERENCE_GRID_GAP = 6
const REFERENCE_GRID_PADDING = 6
const REFERENCE_GROUP_HEADER_HEIGHT = 32
const REFERENCE_GROUP_GAP = 8
const REFERENCE_GRID_OVERSCAN_ROWS = 2
const EMPTY_REFERENCE_GROUPS_STATE = {
  uploaded: [],
  shots: [],
  locations: [],
  props: [],
  characters: [],
  total: 0
}
const EMPTY_REFERENCE_GROUP_SECTIONS = []
const SEARCH_DEBOUNCE_MS = 120

let referenceImageObserver = null
const referenceImageCallbacks = new WeakMap()

const blurActiveElement = () => {
  if (typeof document === 'undefined') return

  const activeElement = document.activeElement
  if (activeElement && typeof activeElement.blur === 'function') {
    activeElement.blur()
  }
}

const getReferenceImageObserver = () => {
  if (
    referenceImageObserver ||
    typeof window === 'undefined' ||
    typeof window.IntersectionObserver !== 'function'
  ) {
    return referenceImageObserver
  }

  referenceImageObserver = new window.IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return

        const onVisible = referenceImageCallbacks.get(entry.target)
        if (onVisible) onVisible()

        referenceImageCallbacks.delete(entry.target)
        referenceImageObserver.unobserve(entry.target)
      })
    },
    { rootMargin: '160px 0px' }
  )

  return referenceImageObserver
}

// ---------------------------------------------------------------------------
// Helper: Image with loading placeholder
// ---------------------------------------------------------------------------
const ImageWithPlaceholder = memo(({ src, alt, aspectRatio, style }) => {
  const [loaded, setLoaded] = useState(false)
  const [w, h] = aspectRatio?.split(':').map(Number) || [1, 1]

  useEffect(() => {
    setLoaded(false)
  }, [src])

  return (
    <div
      style={{
        ...style,
        position: 'relative',
        backgroundColor: '#f0f0f0',
        aspectRatio: `${w}/${h}`,
        overflow: 'hidden'
      }}
    >
      {!loaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '12px',
            zIndex: 1
          }}
        />
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          width: '100%'
        }}
      >
        <ImagePreview
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          style={{
            ...style,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.15s ease-out'
          }}
        />
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const COLORS = {
  primary: '#5057d5',
  primaryLight: '#e8f0fe',
  surface: '#ffffff',
  background: '#f8f9fa',
  text: '#202124',
  textSecondary: '#5f6368',
  textMuted: '#80868b',
  border: '#dadce0',
  borderLight: '#e8eaed',
  success: '#1e8e3e',
  error: '#d93025',
  white: '#ffffff'
}

const getReferenceGroupIcon = groupTitle => {
  if (groupTitle === 'Locations') return 'environment'
  if (groupTitle === 'Characters') return 'user'
  if (groupTitle === 'Props') return 'appstore'
  if (groupTitle === 'Uploaded') return 'cloud-upload'
  return 'picture'
}

const findFirstVisibleRowIndex = (rows, visibleStart) => {
  let low = 0
  let high = rows.length

  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    const row = rows[mid]

    if (row.top + row.height < visibleStart) {
      low = mid + 1
    } else {
      high = mid
    }
  }

  return low
}

const LazyReferenceImage = memo(({ src, alt }) => {
  const containerRef = useRef(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(false)
    const element = containerRef.current
    const observer = getReferenceImageObserver()

    if (!element || !observer) {
      setShouldLoad(true)
      return undefined
    }

    referenceImageCallbacks.set(element, () => setShouldLoad(true))
    observer.observe(element)

    return () => {
      referenceImageCallbacks.delete(element)
      observer.unobserve(element)
    }
  }, [src])

  return (
    <div
      ref={containerRef}
      className={`reference-image-thumbnail${
        isLoaded ? ' reference-image-thumbnail--loaded' : ''
      }`}
      style={{
        width: '100%',
        aspectRatio: '1/1',
        background: COLORS.background
      }}
    >
      {shouldLoad && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.15s ease-out'
          }}
        />
      )}
    </div>
  )
})

const ReferenceImageItem = memo(
  ({ url, title, selected, isGenerating, onSelect, onAnnotate }) => (
    <div
      className="reference-image-item"
      style={{
        position: 'relative',
        cursor: isGenerating ? 'not-allowed' : 'pointer',
        opacity: isGenerating ? 0.5 : 1,
        borderRadius: 6,
        overflow: 'hidden',
        border: `2px solid ${selected ? COLORS.primary : 'transparent'}`,
        transition: 'border-color 0.2s'
      }}
      onClick={() => onSelect(url)}
    >
      <LazyReferenceImage src={url} alt={title || 'Reference image'} />
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 4px 4px',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
          color: '#fff',
          fontSize: 9,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {title || 'Image'}
      </div>
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: COLORS.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon type="check" style={{ color: '#fff', fontSize: 9 }} />
        </div>
      )}

      {!isGenerating && (
        <div
          className="annotate-overlay-btn"
          style={{ position: 'absolute', top: 4, left: 4 }}
          onClick={e => {
            e.stopPropagation()
            onAnnotate(url)
          }}
        >
          <Tooltip title="Annotate Image">
            <Button
              size="small"
              shape="circle"
              icon="highlight"
              style={{
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                color: COLORS.text,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
)

const VirtualizedReferenceGroups = memo(
  ({ groups, selectedReferenceUrls, isGenerating, onSelect, onAnnotate }) => {
    const viewportRef = useRef(null)
    const scrollFrameRef = useRef(null)
    const [viewportWidth, setViewportWidth] = useState(0)
    const [viewportHeight, setViewportHeight] = useState(0)
    const [scrollTop, setScrollTop] = useState(0)

    useEffect(() => {
      const viewport = viewportRef.current
      if (!viewport) return undefined

      const updateViewportSize = () => {
        setViewportWidth(currentWidth =>
          currentWidth === viewport.clientWidth
            ? currentWidth
            : viewport.clientWidth
        )
        setViewportHeight(currentHeight =>
          currentHeight === viewport.clientHeight
            ? currentHeight
            : viewport.clientHeight
        )
      }

      updateViewportSize()

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(updateViewportSize)
        resizeObserver.observe(viewport)
        return () => resizeObserver.disconnect()
      }

      window.addEventListener('resize', updateViewportSize)
      return () => window.removeEventListener('resize', updateViewportSize)
    }, [])

    useEffect(() => {
      setScrollTop(0)
      if (viewportRef.current) viewportRef.current.scrollTop = 0
    }, [groups])

    useEffect(
      () => () => {
        if (scrollFrameRef.current) {
          cancelAnimationFrame(scrollFrameRef.current)
        }
      },
      []
    )

    const handleScroll = useCallback(event => {
      const nextScrollTop = event.currentTarget.scrollTop

      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current)
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        setScrollTop(nextScrollTop)
        scrollFrameRef.current = null
      })
    }, [])

    const effectiveWidth = viewportWidth || 204
    const availableWidth =
      effectiveWidth - REFERENCE_GRID_PADDING * 2 - REFERENCE_GRID_GAP
    const cellSize = Math.max(1, availableWidth / REFERENCE_GRID_COLUMNS)
    const rowHeight = cellSize + REFERENCE_GRID_GAP

    const { contentHeight, headerItems, rowItems } = useMemo(() => {
      const nextHeaderItems = []
      const nextRowItems = []
      let nextContentHeight = REFERENCE_GRID_PADDING

      groups.forEach(group => {
        if (!group.images.length) return

        nextHeaderItems.push({
          key: `${group.key}-header`,
          top: nextContentHeight,
          height: REFERENCE_GROUP_HEADER_HEIGHT,
          group
        })
        nextContentHeight += REFERENCE_GROUP_HEADER_HEIGHT

        const rowCount = Math.ceil(group.images.length / REFERENCE_GRID_COLUMNS)

        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
          nextRowItems.push({
            key: `${group.key}-row-${rowIndex}`,
            top: nextContentHeight,
            height: cellSize,
            group,
            startIndex: rowIndex * REFERENCE_GRID_COLUMNS
          })
          nextContentHeight += rowHeight
        }

        if (rowCount > 0) nextContentHeight -= REFERENCE_GRID_GAP
        nextContentHeight += REFERENCE_GROUP_GAP
      })

      if (nextHeaderItems.length > 0) {
        nextContentHeight += REFERENCE_GRID_PADDING - REFERENCE_GROUP_GAP
      } else {
        nextContentHeight = REFERENCE_GRID_PADDING
      }

      return {
        contentHeight: nextContentHeight,
        headerItems: nextHeaderItems,
        rowItems: nextRowItems
      }
    }, [groups, cellSize, rowHeight])

    const visibleRows = useMemo(() => {
      const measuredViewportHeight = viewportHeight || 400
      const overscanDistance = rowHeight * REFERENCE_GRID_OVERSCAN_ROWS
      const visibleStart = Math.max(0, scrollTop - overscanDistance)
      const visibleEnd = scrollTop + measuredViewportHeight + overscanDistance
      const firstIndex = findFirstVisibleRowIndex(rowItems, visibleStart)
      const rows = []

      for (let index = firstIndex; index < rowItems.length; index++) {
        const row = rowItems[index]
        if (row.top > visibleEnd) break
        rows.push(row)
      }

      return rows
    }, [rowItems, rowHeight, scrollTop, viewportHeight])

    const selectedCountsByGroup = useMemo(() => {
      const counts = {}

      groups.forEach(group => {
        counts[group.key] = group.images.reduce(
          (count, image) =>
            selectedReferenceUrls.has(image.url) ? count + 1 : count,
          0
        )
      })

      return counts
    }, [groups, selectedReferenceUrls])

    return (
      <div
        ref={viewportRef}
        onScroll={handleScroll}
        style={{
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain'
        }}
      >
        <div style={{ position: 'relative', height: contentHeight }}>
          {headerItems.map(item => (
            <div
              key={item.key}
              style={{
                position: 'absolute',
                top: item.top,
                left: REFERENCE_GRID_PADDING,
                right: REFERENCE_GRID_PADDING,
                height: item.height,
                padding: '0 4px',
                background: COLORS.background,
                borderRadius: 5,
                color: COLORS.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 500,
                zIndex: 2
              }}
            >
              <Icon
                type={getReferenceGroupIcon(item.group.title)}
                style={{ fontSize: 12 }}
              />
              <span>{item.group.title}</span>
              <span style={{ color: COLORS.textMuted }}>
                ({item.group.images.length})
              </span>
              {selectedCountsByGroup[item.group.key] > 0 && (
                <Badge
                  count={selectedCountsByGroup[item.group.key]}
                  style={{ backgroundColor: COLORS.primary }}
                />
              )}
            </div>
          ))}

          {visibleRows.map(item => (
            <div
              key={item.key}
              style={{
                position: 'absolute',
                top: item.top,
                left: REFERENCE_GRID_PADDING,
                right: REFERENCE_GRID_PADDING,
                height: item.height,
                display: 'grid',
                gridTemplateColumns: `repeat(${REFERENCE_GRID_COLUMNS}, 1fr)`,
                gap: REFERENCE_GRID_GAP,
                zIndex: 1
              }}
            >
              {item.group.images
                .slice(
                  item.startIndex,
                  item.startIndex + REFERENCE_GRID_COLUMNS
                )
                .map(image => (
                  <ReferenceImageItem
                    key={image.url}
                    url={image.url}
                    title={image.shotTitle}
                    selected={selectedReferenceUrls.has(image.url)}
                    isGenerating={isGenerating}
                    onSelect={onSelect}
                    onAnnotate={onAnnotate}
                  />
                ))}
            </div>
          ))}
        </div>
      </div>
    )
  }
)

/*
 * All reference groups share one virtualized scroll surface. This keeps
 * section context visible in sequence while limiting mounted 8K thumbnails
 * to the rows currently near the viewport.
 */
const ReferenceGroups = memo(
  ({ groups, selectedReferenceUrls, isGenerating, onSelect, onAnnotate }) => (
    <VirtualizedReferenceGroups
      groups={groups}
      selectedReferenceUrls={selectedReferenceUrls}
      isGenerating={isGenerating}
      onSelect={onSelect}
      onAnnotate={onAnnotate}
    />
  )
)

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const ImageEditorModalContent = ({
  visible,
  mode = 'kontext',
  onCancel,
  onConfirm,
  initialPrompt = '',
  referenceImages = EMPTY_REFERENCE_IMAGES,
  aspectRatio,
  currentImage = null,
  videoId,
  creativeDirection = null
}) => {
  // Core state
  const [prompt, setPrompt] = useState('')
  const [currentEditingImage, setCurrentEditingImage] = useState(currentImage)
  const [selectedReferences, setSelectedReferences] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [conversation, setConversation] = useState([])
  const [selectedForSave, setSelectedForSave] = useState(null)

  // UI state
  const [showReferences, setShowReferences] = useState(true)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const [annotatingImageSrc, setAnnotatingImageSrc] = useState(null)

  // Advanced settings
  const [inferenceSteps, setInferenceSteps] = useState(28)
  const [guidanceScale, setGuidanceScale] = useState(0.85)

  // New feature state
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [uploadedImages, setUploadedImages] = useState([])
  const [referencePanelReady, setReferencePanelReady] = useState(false)

  const chatEndRef = useRef(null)
  const mountedRef = useRef(false)
  const generationTokenRef = useRef(0)
  const isSendingRef = useRef(false)

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  const scrollToBottom = () => {
    chatEndRef.current &&
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      isSendingRef.current = false
    }
  }, [])

  useEffect(() => {
    if (visible) {
      setPrompt('')
      setConversation([])
      setSelectedForSave(null)
      setShowReferences(true)
      setCurrentEditingImage(currentImage)
      setSelectedReferences([])
      setSearchTerm('')
      setDebouncedSearchTerm('')
      setUploadedImages([])
      setReferencePanelReady(false)
    }
  }, [visible, initialPrompt, mode, currentImage])

  useEffect(() => {
    if (!visible) return undefined

    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [visible, searchTerm])

  useEffect(() => {
    if (!visible || !showReferences) {
      setReferencePanelReady(false)
      return undefined
    }

    const frameId =
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(() => setReferencePanelReady(true))
        : setTimeout(() => setReferencePanelReady(true), 0)

    return () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.cancelAnimationFrame === 'function'
      ) {
        window.cancelAnimationFrame(frameId)
      } else {
        clearTimeout(frameId)
      }
    }
  }, [visible, showReferences])

  // ---------------------------------------------------------------------------
  // Task polling
  // ---------------------------------------------------------------------------
  const isCurrentGeneration = useCallback(
    token => mountedRef.current && generationTokenRef.current === token,
    []
  )

  const waitForNextPoll = ms =>
    new Promise(resolve => setTimeout(resolve, ms))

  const pollTask = async taskId => {
    const POLL_INTERVAL = 5000
    const MAX_ATTEMPTS = 60
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        const res = await getTaskStatus(taskId)
        const status = res?.data?.status
        if (status === 'success') return res?.data?.output
        if (status === 'failed' || status === 'error') {
          throw new Error(res?.data?.error || 'Task failed')
        }
        await waitForNextPoll(POLL_INTERVAL)
      } catch (err) {
        if (err.message !== 'Task failed') console.error('Polling error', err)
        throw err
      }
    }
    throw new Error('Timeout')
  }

  // ---------------------------------------------------------------------------
  // Send prompt to AI
  // ---------------------------------------------------------------------------
  const handleSend = async () => {
    const promptText = prompt.trim()

    if (isSendingRef.current || isGenerating) return

    if (!promptText) {
      message.error('Please enter a prompt')
      return
    }

    const runToken = generationTokenRef.current + 1
    generationTokenRef.current = runToken
    isSendingRef.current = true

    const usedReferences = [
      currentEditingImage,
      ...(selectedReferences || [])
    ].filter(Boolean)

    setConversation(prev => [
      ...prev,
      {
        type: 'user',
        content: promptText,
        timestamp: Date.now(),
        references: usedReferences
      }
    ])

    const loadingId = Date.now()
    setConversation(prev => [...prev, { type: 'loading', id: loadingId }])
    setIsGenerating(true)
    setPrompt('')

    try {
      let response
      if (mode === 'kontext') {
        response = await editImageDeepAI({
          prompt: promptText,
          image_to_be_edited: currentEditingImage,
          references: selectedReferences || [],
          num_inference_steps: inferenceSteps,
          guidance_scale: guidanceScale,
          ratio: aspectRatio,
          video_id: videoId,
          ...createCreativeGuidelineSessionPayload({ creativeDirection })
        })
      } else {
        response = await generateImageWithReferenceNanoBanana({
          prompt: promptText,
          ratio: aspectRatio,
          image_to_be_edited: currentEditingImage,
          reference_images: selectedReferences || [],
          video_id: videoId,
          ...createCreativeGuidelineSessionPayload({ creativeDirection })
        })
      }

      if (!isCurrentGeneration(runToken)) return

      const taskId = response?.data?.task_id
      if (!taskId) throw new Error('No task ID')

      const resultUrl = await pollTask(taskId)

      if (!isCurrentGeneration(runToken)) return

      setConversation(prev =>
        prev
          .filter(msg => msg.id !== loadingId)
          .concat({
            type: 'assistant',
            content: resultUrl,
            timestamp: Date.now()
          })
      )
      setSelectedForSave(resultUrl)
      setCurrentEditingImage(resultUrl)
      setSelectedReferences([])
    } catch (error) {
      if (!isCurrentGeneration(runToken)) return

      setConversation(prev =>
        prev
          .filter(msg => msg.id !== loadingId)
          .concat({
            type: 'error',
            content: error.message || 'Generation failed',
            timestamp: Date.now()
          })
      )
    } finally {
      if (isCurrentGeneration(runToken)) {
        setIsGenerating(false)
        isSendingRef.current = false
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleImageSelect = url => {
    setSelectedForSave(url)
    setCurrentEditingImage(url)
  }

  const handleSave = () => {
    if (selectedForSave) setShowConfirmSave(true)
  }

  const confirmSave = () => {
    if (selectedForSave) {
      blurActiveElement()
      onConfirm(selectedForSave)
      setShowConfirmSave(false)
    }
  }

  const handleReferenceClick = useCallback(
    url => {
      if (isGenerating) return

      setSelectedReferences(previousReferences => {
        const current = Array.isArray(previousReferences)
          ? previousReferences
          : []

        return current.includes(url)
          ? current.filter(referenceUrl => referenceUrl !== url)
          : [...current, url]
      })
    },
    [isGenerating]
  )

  const handleAnnotate = useCallback(url => {
    blurActiveElement()
    setAnnotatingImageSrc(url)
  }, [])

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Upload complete handler — add to uploaded images and auto-select
  const handleUploadComplete = url => {
    setUploadedImages(prev => [
      ...prev,
      { url, shotTitle: 'Uploaded', sceneTitle: 'Uploaded' }
    ])
    handleReferenceClick(url)
  }

  // Drawing canvas done handler
  const handleDrawingDone = annotatedUrl => {
    if (annotatingImageSrc === currentEditingImage) {
      setCurrentEditingImage(annotatedUrl)
    } else {
      setUploadedImages(prev => [
        { url: annotatedUrl, shotTitle: 'Annotated', sceneTitle: 'Uploaded' },
        ...prev
      ])
      setSelectedReferences(prev => {
        const current = Array.isArray(prev) ? prev : []
        if (!current.includes(annotatedUrl)) return [...current, annotatedUrl]
        return current
      })
    }
    setAnnotatingImageSrc(null)
  }

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const selectedCount = (selectedReferences || []).length
  const selectedReferenceUrls = useMemo(
    () => new Set(selectedReferences || []),
    [selectedReferences]
  )
  const shouldPrepareReferences =
    visible && showReferences && referencePanelReady
  const sourceReferenceImages = useMemo(
    () =>
      Array.isArray(referenceImages) ? referenceImages : EMPTY_REFERENCE_IMAGES,
    [referenceImages]
  )

  const generatedImages = useMemo(
    () =>
      conversation
        .filter(msg => msg.type === 'assistant')
        .map(msg => ({ url: msg.content, timestamp: msg.timestamp })),
    [conversation]
  )

  const referenceGroups = useMemo(() => {
    if (!shouldPrepareReferences) return EMPTY_REFERENCE_GROUPS_STATE

    const normalizedSearchTerm = debouncedSearchTerm.trim().toLowerCase()
    const seenUrls = new Set()
    const validReferenceImages = sourceReferenceImages.filter(image => {
      if (
        !image ||
        !image.url ||
        image.url === currentEditingImage ||
        seenUrls.has(image.url)
      ) {
        return false
      }

      seenUrls.add(image.url)
      return true
    })

    const matchesSearch = image =>
      !normalizedSearchTerm ||
      (image.shotTitle || '').toLowerCase().includes(normalizedSearchTerm) ||
      (image.sceneTitle || '').toLowerCase().includes(normalizedSearchTerm)

    const filteredReferenceImages = validReferenceImages.filter(matchesSearch)
    const filteredUploadedImages = uploadedImages.filter(
      image =>
        !normalizedSearchTerm ||
        'uploaded'.includes(normalizedSearchTerm) ||
        matchesSearch(image)
    )

    return {
      uploaded: filteredUploadedImages,
      shots: filteredReferenceImages.filter(
        image =>
          image.sceneTitle !== 'Location' &&
          image.sceneTitle !== 'Character' &&
          image.sceneTitle !== 'Prop'
      ),
      locations: filteredReferenceImages.filter(
        image => image.sceneTitle === 'Location'
      ),
      props: filteredReferenceImages.filter(
        image => image.sceneTitle === 'Prop'
      ),
      characters: filteredReferenceImages.filter(
        image => image.sceneTitle === 'Character'
      ),
      total: filteredReferenceImages.length + filteredUploadedImages.length
    }
  }, [
    shouldPrepareReferences,
    sourceReferenceImages,
    currentEditingImage,
    debouncedSearchTerm,
    uploadedImages
  ])

  const referenceGroupSections = useMemo(() => {
    if (!shouldPrepareReferences) return EMPTY_REFERENCE_GROUP_SECTIONS

    return [
      {
        key: 'uploaded',
        title: 'Uploaded',
        images: referenceGroups.uploaded
      },
      { key: 'shots', title: 'Shots', images: referenceGroups.shots },
      {
        key: 'locations',
        title: 'Locations',
        images: referenceGroups.locations
      },
      { key: 'props', title: 'Props', images: referenceGroups.props },
      {
        key: 'characters',
        title: 'Characters',
        images: referenceGroups.characters
      }
    ].filter(group => group.images.length > 0)
  }, [referenceGroups, shouldPrepareReferences])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Modal
      visible={visible}
      title={null}
      centered
      onCancel={() => {
        blurActiveElement()
        onCancel()
      }}
      afterClose={blurActiveElement}
      width={1000}
      maskClosable={false}
      destroyOnClose
      bodyStyle={{
        padding: 0,
        height: '75vh',
        display: 'flex',
        flexDirection: 'column'
      }}
      footer={null}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: COLORS.white
        }}
      >
        <div style={{ fontWeight: 500, fontSize: 15, color: COLORS.text }}>
          Image Editor
          <span
            style={{
              marginLeft: 8,
              fontSize: 12,
              color: COLORS.textMuted,
              fontWeight: 400
            }}
          >
            ({mode === 'kontext' ? 'Single Reference' : 'Multi-Reference'})
          </span>
          <hr />
          <p style={{ fontSize: 12, color: COLORS.textMuted }}>
            Chat with AI to edit your image
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Chat Area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: COLORS.white,
            margin: '24px 32px'
          }}
        >
          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 32px',
              background: '#f9f9f9ff',
              borderRadius: '20px'
            }}
          >
            {/* Empty State */}
            {conversation.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  color: COLORS.textMuted,
                  height: '100%',
                  justifyContent: 'center',
                  padding: '24px 32px',
                  borderRadius: '20px'
                }}
              >
                <div
                  style={{
                    height: 48,
                    width: 48,
                    background: '#5057d5',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16
                  }}
                >
                  <Icon type="file-image" style={{ color: '#fff' }} size={24} />
                </div>

                <div style={{ fontSize: 16, marginBottom: 16 }}>
                  {mode === 'kontext'
                    ? 'Describe your edit'
                    : 'Select references and describe what to generate'}
                </div>
                {currentEditingImage && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      padding: 12,
                      background: COLORS.white,
                      borderRadius: 12,
                      border: `1px solid ${COLORS.borderLight}`
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: COLORS.textMuted
                      }}
                    >
                      Image being edited
                    </div>
                    <div style={{ position: 'relative' }}>
                      <ImagePreview
                        src={currentEditingImage}
                        alt="Current"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <Tooltip title="Annotate">
                          <Button
                            size="small"
                            shape="circle"
                            icon="highlight"
                            onClick={() => {
                              blurActiveElement()
                              setAnnotatingImageSrc(currentEditingImage)
                            }}
                            style={{
                              background: 'rgba(255,255,255,0.9)',
                              border: 'none',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                    {initialPrompt && (
                      <div
                        style={{
                          fontSize: 12,
                          color: COLORS.textSecondary,
                          fontStyle: 'italic',
                          textAlign: 'center',
                          marginTop: 4,
                          maxWidth: 300
                        }}
                      >
                        "{initialPrompt}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            {conversation.map((msg, idx) => (
              <div key={idx} style={{ marginBottom: 24 }}>
                {/* User Message */}
                {msg.type === 'user' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div
                      style={{
                        maxWidth: '65%',
                        background: COLORS.primaryLight,
                        padding: '14px 18px',
                        borderRadius: '20px 20px 4px 20px',
                        color: COLORS.text
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                        {msg.content}
                      </div>
                      {msg.references?.filter(r => r).length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                          {msg.references
                            .filter(r => r)
                            .map((ref, i) => (
                              <ImagePreview
                                key={i}
                                src={ref}
                                alt=""
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 4,
                                  objectFit: 'cover'
                                }}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Loading */}
                {msg.type === 'loading' && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <div
                      style={{
                        background: '#fff',
                        padding: '14px 20px',
                        borderRadius: '4px 20px 20px 20px',
                        border: `1px solid ${COLORS.borderLight}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                    >
                      <Spin size="small" />
                      <span
                        style={{ color: COLORS.textSecondary, fontSize: 14 }}
                      >
                        Generating...
                      </span>
                    </div>
                  </div>
                )}

                {/* Assistant Response */}
                {msg.type === 'assistant' && (
                  <div style={{ display: 'flex' }}>
                    <div
                      style={{
                        background: COLORS.white,
                        padding: 12,
                        borderRadius: '4px 20px 20px 20px',
                        border: `1px solid ${COLORS.border}`
                      }}
                    >
                      <ImageWithPlaceholder
                        src={msg.content}
                        alt="Generated"
                        aspectRatio={aspectRatio}
                        style={{
                          width: 260,
                          maxWidth: '100%',
                          borderRadius: 8,
                          display: 'block'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Error */}
                {msg.type === 'error' && (
                  <div
                    style={{
                      background: '#fef2f2',
                      padding: '12px 16px',
                      borderRadius: '4px 20px 20px 20px',
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      fontSize: 14,
                      display: 'inline-block'
                    }}
                  >
                    {msg.content}
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div style={{ padding: '16px 0', background: COLORS.white }}>
            {/* Advanced Settings */}
            {mode === 'kontext' && (
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: COLORS.textMuted,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  <Icon
                    type={showAdvanced ? 'down' : 'right'}
                    style={{ fontSize: 10 }}
                  />
                  Advanced
                </div>
                {showAdvanced && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 16,
                      marginTop: 10,
                      padding: 12,
                      background: COLORS.background,
                      borderRadius: 6
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: COLORS.textMuted,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        Steps: {inferenceSteps}
                        <Tooltip title="Number of denoising steps. Higher values produce more detailed results but take longer.">
                          <Icon
                            type="info-circle"
                            style={{ fontSize: 11, cursor: 'help' }}
                          />
                        </Tooltip>
                      </div>
                      <Slider
                        min={10}
                        max={50}
                        value={inferenceSteps}
                        onChange={setInferenceSteps}
                        disabled={isGenerating}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: COLORS.textMuted,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        Guidance: {guidanceScale.toFixed(2)}
                        <Tooltip title="Controls how closely the output follows the prompt. Higher values = stronger adherence.">
                          <Icon
                            type="info-circle"
                            style={{ fontSize: 11, cursor: 'help' }}
                          />
                        </Tooltip>
                      </div>
                      <Slider
                        min={0.1}
                        max={2.0}
                        step={0.05}
                        value={guidanceScale}
                        onChange={setGuidanceScale}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.white
                }}
              >
                <TextArea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe your edit..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  disabled={isGenerating}
                  style={{
                    borderRadius: 8,
                    padding: '10px 44px 10px 14px',
                    resize: 'none',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: 8,
                    bottom: 6,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ImageUploader
                    videoId={videoId}
                    aspectRatio={aspectRatio}
                    onUploadComplete={handleUploadComplete}
                    disabled={isGenerating}
                    buttonStyle="ghost"
                  />
                </div>
              </div>

              <Button
                type="primary"
                onClick={handleSend}
                loading={isGenerating}
                style={{
                  height: 40,
                  padding: '0 20px',
                  background: COLORS.primary,
                  borderColor: COLORS.primary
                }}
              >
                Send
              </Button>
              {generatedImages.length > 0 && (
                <Button
                  icon="history"
                  onClick={() => setShowHistory(true)}
                  style={{ height: 40 }}
                >
                  History ({generatedImages.length})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Reference Panel */}
        <div
          style={{
            width: showReferences ? 220 : 44,
            borderLeft: `1px solid ${COLORS.borderLight}`,
            background: COLORS.white,
            transition: 'width 0.2s',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Panel header */}
          <div
            style={{
              padding: 12,
              borderBottom: `1px solid ${COLORS.borderLight}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: showReferences ? 'space-between' : 'center'
            }}
            onClick={() => setShowReferences(!showReferences)}
          >
            {showReferences ? (
              <>
                <span
                  style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}
                >
                  References
                  {selectedCount > 0 && (
                    <Badge
                      count={selectedCount}
                      style={{ marginLeft: 8, backgroundColor: COLORS.primary }}
                    />
                  )}
                </span>
                <Icon
                  type="right"
                  style={{ color: COLORS.textMuted, fontSize: 12 }}
                />
              </>
            ) : (
              <Badge count={selectedCount} offset={[-4, 0]}>
                <Icon
                  type="picture"
                  style={{ fontSize: 16, color: COLORS.textMuted }}
                />
              </Badge>
            )}
          </div>

          {showReferences && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Search + Upload row */}
              <div
                style={{
                  padding: '10px 10px',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  borderBottom: '1px solid #eef0f4'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ReferenceSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                  />
                </div>
              </div>

              {/* Image lists */}
              <div style={{ flex: 1, minHeight: 0 }}>
                {!referencePanelReady ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 20
                    }}
                  >
                    <Spin size="small" />
                  </div>
                ) : referenceGroups.total === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: 20,
                      color: COLORS.textMuted,
                      fontSize: 12
                    }}
                  >
                    {searchTerm ? 'No matches' : 'No references'}
                  </div>
                ) : (
                  <ReferenceGroups
                    groups={referenceGroupSections}
                    selectedReferenceUrls={selectedReferenceUrls}
                    isGenerating={isGenerating}
                    onSelect={handleReferenceClick}
                    onAnnotate={handleAnnotate}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 24px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: COLORS.white
        }}
      >
        <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
          {selectedForSave ? (
            <>
              <Icon
                type="check-circle"
                theme="filled"
                style={{ color: COLORS.success, marginRight: 6 }}
              />
              Image ready to save
            </>
          ) : (
            'Select an image from History to save'
          )}
        </span>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            onClick={() => {
              blurActiveElement()
              onCancel()
            }}
            disabled={isGenerating}
            style={{ height: 36 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!selectedForSave || isGenerating}
            style={{
              height: 36,
              background: COLORS.primary,
              borderColor: COLORS.primary,
              fontWeight: 500
            }}
          >
            Save Image
          </Button>
        </div>
      </div>

      {/* History Modal */}
      <Modal
        maskClosable={false}
        title="Select Generated Image"
        visible={showHistory}
        onCancel={() => setShowHistory(false)}
        footer={null}
        width={600}
        centered
      >
        {generatedImages.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 40,
              color: COLORS.textMuted
            }}
          >
            No images generated yet
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
              maxHeight: '60vh',
              overflowY: 'auto'
            }}
          >
            {generatedImages.map((img, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border:
                    selectedForSave === img.url
                      ? `2px solid ${COLORS.primary}`
                      : '2px solid transparent'
                }}
                onClick={() => {
                  handleImageSelect(img.url)
                  setShowHistory(false)
                }}
              >
                <img
                  src={img.url}
                  alt="Generated"
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: '100%',
                    aspectRatio: '1/1',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                {selectedForSave === img.url && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Icon
                      type="check"
                      style={{ color: '#fff', fontSize: 12 }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Save Confirmation Modal */}
      <Modal
        maskClosable={false}
        title="Confirm Image Replacement"
        visible={showConfirmSave}
        onCancel={() => setShowConfirmSave(false)}
        centered
        width={520}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmSave(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={confirmSave}
            style={{ background: COLORS.primary, borderColor: COLORS.primary }}
          >
            Confirm & Save
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              fontSize: 14,
              color: COLORS.textSecondary,
              marginBottom: 20
            }}
          >
            This action will replace the current shot image with your selected
            image.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 20,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Current Image */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.textMuted,
                  marginBottom: 8,
                  fontWeight: 500
                }}
              >
                Current Image
              </div>
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.background
                }}
              >
                {currentImage ? (
                  <img
                    src={currentImage}
                    alt="Current"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: COLORS.textMuted,
                      fontSize: 12
                    }}
                  >
                    No image
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div style={{ color: COLORS.textMuted }}>
              <Icon type="arrow-right" style={{ fontSize: 24 }} />
            </div>

            {/* New Image */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.primary,
                  marginBottom: 8,
                  fontWeight: 500
                }}
              >
                New Image
              </div>
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: `2px solid ${COLORS.primary}`,
                  boxShadow: '0 2px 8px rgba(26, 115, 232, 0.2)'
                }}
              >
                {selectedForSave && (
                  <img
                    src={selectedForSave}
                    alt="New"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Drawing Canvas Modal */}
      {annotatingImageSrc ? (
        <DrawingCanvas
          visible
          imageSrc={annotatingImageSrc}
          onDone={handleDrawingDone}
          onCancel={() => {
            blurActiveElement()
            setAnnotatingImageSrc(null)
          }}
          videoId={videoId}
        />
      ) : null}
    </Modal>
  )
}

const ImageEditorModal = memo(props => {
  if (!props.visible) return null

  return <ImageEditorModalContent {...props} />
})

export default ImageEditorModal
